import React, { useState, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import axios from 'axios';
import CryptoJS from 'crypto-js';
import styles from './Sms.module.scss';
import { Register } from '../Register';
import { useTranslation } from 'react-i18next';
import { IinContext } from '../../context/IinContext';
import { AuthTabs, PasswordForm, VerificationCodeInput, EmailConfirmation } from '../../components';
import { CODE_LENGTH, getCodeErrorMessage, isCodeComplete, sanitizeCode } from '../../utils/verificationCode';
import Cookies from 'js-cookie';

export const Sms = () => {
  const { t } = useTranslation();
  const [password, setPassword] = useState('');
  const [email, setEmail] = useState('');
  const [emailVerified, setEmailVerified] = useState(true);
  const [error, setError] = useState('');
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingBMG, setIsCheckingBMG] = useState(false);
  const [isCodeFormVisible, setIsCodeFormVisible] = useState(false);
  const [isEmailConfirmationVisible, setIsEmailConfirmationVisible] = useState(false);
  const [isVerificationCodeInput, setIsVerificationCodeInput] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showResendButton, setShowResendButton] = useState(false);
  const [isPasswordFormVisible, setIsPasswordFormVisible] = useState(false);
  const [showRegisterForm, setShowRegisterForm] = useState(false);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState('');
  const [showLoginForm, setShowLoginForm] = useState(false);
  const [firstRequestSent, setFirstRequestSent] = useState(false);
  const [timer, setTimer] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isCmcPage = location.pathname === '/sms';
  const [resendTimer, setResendTimer] = useState(120);
  const [isResendDisabled, setIsResendDisabled] = useState(true);
  const [lastDigits, setLastDigits] = useState(null);
  const [isUpdateNumberDisabled, setIsUpdateNumberDisabled] = useState(false);

  const [isEmailConfirmed, setIsEmailConfirmed] = useState(false);
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [activeTab, setActiveTab] = useState('/sms');

  const { iin, setIin, setIinOrBin } = useContext(IinContext);

  const handleIinKeyDown = (e) => {
    if (e.key === 'Enter' && iin.length === 12 && !isButtonDisabled) {
      handleButtonClick();
    }
  };

  const handleCodeKeyDown = (e) => {
    if (e.key === 'Enter' && code) {
      handleCodeSubmit(code);
    }
  };

  const handleEmailKeyDown = (e) => {
    if (e.key === 'Enter' && email) {
      handleConfirmEmail();
    }
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const referrer = sessionStorage.getItem('ref');
  }, []);

  useEffect(() => {
    const validateExistingSecretCode = async () => {
      const existingSecretCode = Cookies.get('secretCode');
      if (existingSecretCode) {
        try {
          const response = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
            secretCode: existingSecretCode
          });
          
          if (response.data === true) {
            await redirectToReferrer(existingSecretCode);
          } else {
            Cookies.remove('secretCode');
          }
        } catch (error) {
          console.error("Ошибка при валидации secretCode:", error);
          Cookies.remove('secretCode');
        }
      }
    };

    validateExistingSecretCode();
  }, []);

  useEffect(() => {
    let interval = null;
    if (timer > 0) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer - 1);
      }, 1000);
    } else if (timer === 0 && firstRequestSent) {
      setIsButtonDisabled(false);
    }
    return () => clearInterval(interval);
  }, [timer, firstRequestSent]);
 
  const handleInputChange = (event) => {
    const value = event.target.value;
    setIinOrBin(value);
    setIin(value);
    setError('');
    setSuccessMessage('');
    setIsButtonDisabled(true);

    if (value.length === 12) {
      const fifthDigit = parseInt(value[4], 10);

      if (!isNaN(fifthDigit)) {
        if (fifthDigit >= 4) {
          setError(t('sms.physPersonOnlyError'));
        } else {
          setIsButtonDisabled(false);
        }
      }
    }
  };

  const handleLoginClick = async (password) => {
    try {
      setError('');
      setIsLoading(true);

      const existingSecretCode = Cookies.get('secretCode');
      if (existingSecretCode) {
        try {
          const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
            secretCode: existingSecretCode
          });
          
          if (validationResponse.data === true) {
            await redirectToReferrer(existingSecretCode);
            setIsLoading(false);
            return;
          } else {
            Cookies.remove('secretCode');
          }
        } catch (error) {
          console.error("Ошибка при валидации secretCode:", error);
          Cookies.remove('secretCode');
        }
      }

      const key = CryptoJS.enc.Hex.parse('10c3417e0b38987f3b44528d999a090efd708bcef15d6357c252bcd42b4cf03d');
      const iv = CryptoJS.enc.Hex.parse('315f48391c3d3cc857128287f8457d47');
 
      const encryptedUsername = CryptoJS.AES.encrypt(iin, key, { iv: iv }).toString();
      const encryptedPassword = CryptoJS.AES.encrypt(password, key, { iv: iv }).toString();
 
      const clientId = sessionStorage.getItem('client-id');
 
      const response = await axios.post('/kcssobackext/sso/external/login', {
        username: encryptedUsername,
        password: encryptedPassword,
        clientId: Number(clientId)
      });

      if (response.data) {
        Cookies.set('secretCode', response.data, { 
          expires: 0.25,
          domain: window.location.hostname,
          path: '/kclssofront',
          sameSite: 'Lax',
          secure: false 
        });
        await redirectToReferrer(response.data);
      } else {
        throw new Error('Secret code not found in response');
      }
 
    } catch (error) {
      if (error.response && error.response.status === 401) {
        setError(t('error.invalidPassword'));
      } else {
        setError(t('error.serverError'));
      }
    } finally {
      setIsLoading(false);
    }
  };
 
  const handleButtonClick = async () => {
    try {
      setIsLoading(true);
      setError('');
      setSuccessMessage('');
      setIsButtonDisabled(true);
 
      const generateResponse = await axios.get(`/kcssobackext/sms/generate-code/${iin}`);
      setSuccessMessage(t('sms.codeSentSuccess'));
      
      setIsCheckingBMG(true);
      checkBMGStatus();
      setFirstRequestSent(true);
      setTimer((prev) => (prev > 0 ? prev : 120));
    } catch (err) {
      if (err.response.data == "You can request only once every 2 minutes") {
        setError(t('sms.codeAlreadySent', { seconds: timer }));
      } else if (err.response.status >= 500) {
        setError(t('error.serverError'));
      } else {
        setError(t('error.unknownError'));
      }

      console.error('Error occurred during SMS code generation:', err);
    } finally {
      setIsLoading(false);
      // setTimer(60);
    }
  };

  const checkBMGStatus = async () => {
    let elapsedSeconds = 0;
    const maxDuration = 60;
  
    const intervalId = setInterval(async () => {
      if (elapsedSeconds >= maxDuration) {
        clearInterval(intervalId);
        setIsCheckingBMG(false);
        setIsLoading(false);
        return;
      }
  
      try {
        const checkResponse = await axios.get(`/kcssobackext/sms/isExistsBMG/${iin}`);
        const { isCompleteResponse, isExists, isEnabled, lastDigits } = checkResponse.data;
  
        if (lastDigits) {
          setLastDigits(lastDigits || null);
        }
        
        if (isCompleteResponse) {
          clearInterval(intervalId);
  
          if (isExists && isEnabled) {
            setIsCodeFormVisible(true);
            setSuccessMessage(t('sms.codeSentSuccess'));
            setError('');
          } else if (isExists && !isEnabled) {
            setError(t('sms.sendingNotPossible'));
          } else if (!isExists && !isEnabled) {
            setError(t('sms.iinNotRegisteredInBMG'));
          } else {
            setError(t('sms.phoneNotRegistered'));
          }
  
          setIsCheckingBMG(false);
          setIsLoading(false);
        }
      } catch (err) {
        clearInterval(intervalId);
        if (err.response && err.response.status >= 500) {
          setError(t('error.serverError'));
        }
        console.error('Error occurred during BMG check:', err);
        setIsCheckingBMG(false);
        setIsLoading(false);
      }
  
      elapsedSeconds += 5;
    }, 5000);
  };

  const handleCodeSubmit = async (code) => {
    setVerificationCode(code);

    if (!code) return;
    if (!isCodeComplete(code)) {
      setCodeError(t('error.invalidCodeFormat'));
      return;
    }

    try {
      const response = await axios.post(
        `/kcssobackext/sso/external/checkVerificationCode`,
        {
          iinOrBin: iin,
          code: code,
          method: isEmailConfirmed ? 'email' : 'sms',
        }
      );

      if (response.data == false) {
        setCodeError(t('error.invalidCode'));
        setShowResendButton(true);
      } else {
        setSuccessMessage(t('sms.codeVerifiedSuccess'));
        setCode('');
        setCodeError('');
        setShowResendButton(false);
        try {
          const getInfoResponse = await axios.get(`/kcssobackext/sso/getInfo?iinOrBin=${iin}`);

          if (getInfoResponse.data.exists === false) {
            setShowRegisterForm(true);
          } else if (getInfoResponse.data.exists === true && getInfoResponse.data.emailVerified === false) {
            setEmail(getInfoResponse.data.email);
            setIsEmailConfirmationVisible(true);
          } else if (getInfoResponse.data.exists === true && getInfoResponse.data.emailVerified === true) {
            setIsPasswordFormVisible(true);
          }
        } catch (infoErr) {
          console.error('Error occurred during getting information:', infoErr);
          setError(t('error.getUserInfoError'));
        }
      }
    } catch (err) {
      if (err.response) {
        const codeErrorMessage = getCodeErrorMessage(err.response.status, err.response.data, t);
        if (codeErrorMessage) {
          setCodeError(codeErrorMessage);
          setShowResendButton(true);
        } else if (err.response.data === 'User is not found in db') {
          setIsCodeFormVisible(false);
          setShowRegisterForm(true);
        } else {
          setError(t('error.codeVerificationError'));
          console.error('Error occurred during code verification:', err);
        }
      }
    }
  };

  const handleResendCode = async () => {
    setResendTimer(120);
    setIsResendDisabled(true);
    setIsCheckingBMG(true);

    try {
      const resendResponse = await axios.post(`/kcssobackext/sms/codeNotDelivered?iin=${iin}`);
  
      if (resendResponse.data === false) {
        setCodeError(t('error.invalidCode'));
      } else if (resendResponse.data === true) {
        setCodeError('');
        setSuccessMessage(t('sms.codeResentSuccess'));
        setResendTimer(120);
        setIsButtonDisabled(true);
        checkBMGStatus();
      }
    } catch (err) {
      if (err.response && (err.response.status === 400 || err.response.status === 500)) {
        setError(t('sms.resendCodeError'));
      } else {
        setError(t('error.serverErrorResend'));
      }
      console.error('Error occurred during code resend:', err);
    }
  };  

  const handleGoHome = () => {
    navigate('/');
  };

  const handleConfirmEmail = async () => {
    setShowResendButton(true);
    
    try {
      const response = await axios.post(`/kcssobackext/sso/external/changeEmail`, {
        iinOrBin: iin,
        email: email
      });

      if (response.status === 200) {
        setShowEmailInput(false);
        setIsEmailConfirmed(true);
        setIsEmailConfirmationVisible(false);
        setShowResendButton(false);
        setIsCodeFormVisible(false);
        setIsVerificationCodeInput(true);
      } else {
        setError(t('sms.confirmationError'));
      }
    } catch (error) {
      setError(t('sms.confirmationError'));
      console.error('Error during email confirmation:', error);
    }
  };

  const handleChangeEmail = () => {
    setIsEmailConfirmationVisible(false);
    setIsCodeFormVisible(false);
    setEmail('');
    setError('');
    setShowEmailInput(true);
  };

  const fetchRedirectUrl = async (clientId) => {
    try {
      const response = await axios.get(`/kcssobackext/client/${clientId}`);
      if (response.data && response.data.redirectUrl) {
        return response.data.redirectUrl;
      } else {
        throw new Error("Redirect URL not found in response");
      }
    } catch (error) {
      console.error("Error fetching redirect URL:", error);
      throw error;
    }
  };

  const sendPostMessage = (secretCode, backurl) => {
    if (window.opener) {
      window.opener.postMessage(
        { 
          type: 'AUTH_SECRET_CODE', 
          secretCode: secretCode,
          backurl: backurl
        },
        "*"
      );
      window.close();
    }
  };

  const redirectToReferrer = async (secretCode) => {
    const clientId = sessionStorage.getItem('client-id');
    const backurl = sessionStorage.getItem('backurl');
    
    if (!clientId) {
      console.error("Client ID is missing");
      return;
    }
  
    try {
      const validationResponse = await axios.post('/kcssobackext/sso/external/validateSecretCode', {
        secretCode: secretCode
      });
      
      if (validationResponse.data !== true) {
        Cookies.remove('secretCode');
        return;
      }

      if (window.opener) {
        sendPostMessage(secretCode, backurl);
        return;
      }

      const redirectUrl = await fetchRedirectUrl(clientId);
  
      const url = new URL(redirectUrl);
      url.searchParams.set('client-id', clientId);
      url.searchParams.set('secret-code', secretCode);
    } catch (error) {
      console.error("Error during redirection:", error);
      navigate('/');
    }
  };

  const handleEmailSubmit = async () => {
    try {
      const response = await axios.post('/kcssobackext/sso/external/changeEmail', {
        iinOrBin: iin,
        email: email
      });

      if (response.status == 200) {
        setShowEmailInput(false);
        setIsCodeFormVisible(true);
      } else {
        // alert(t('error.sendCodeError'));
      }
    } catch (error) {
      console.error(t('error.sendCodeError'), error);
      // alert(t('error.sendCodeError'));
    }
  };

  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    } else {
      setIsResendDisabled(false);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  const handleResendClick = () => {
    setResendTimer(120);
    setIsResendDisabled(true);

    handleResendCode();
  };

  const handleUpdateNumberFromBMG = async () => {
    try {
      setIsLoading(true);
      setError('');
      
      const response = await axios.post(
        `/kcssobackext/sms/update-number/${iin}`,
        {},
        {
          headers: {
            'accept': '*/*'
          }
        }
      );

      if (response.status === 200) {
        alert(t('sms.updateNumberRequestSent'));
        setIsUpdateNumberDisabled(true);
  
        const pollPhoneStatus = async () => {
          try {
            const statusResponse = await axios.get(
              `/kcssobackext/sms/phone-status/${iin}`,
              {
                headers: {
                  'accept': '*/*'
                }
              }
            );
  
            if (statusResponse.data.status === 'NOT_FOUND' || statusResponse.data.status === 'SUCCESS') {
              clearInterval(pollingInterval);
              if (statusResponse.data.status === 'SUCCESS') {
                alert(t('sms.updateNumberSuccess'));
                setLastDigits(statusResponse.data.lastDigits);
                setIsResendDisabled(false);
              } else if (statusResponse.data.status === 'NOT_FOUND') {
                alert(t('sms.updateNumberNotFound'));
              } else {
                setError(t('sms.updateNumberNotFound'));
              }
            }
          } catch (err) {
            console.error('Error checking phone status:', err);
          }
        };
  
        const pollingInterval = setInterval(pollPhoneStatus, 5000);
  
        pollPhoneStatus();
  
        return () => clearInterval(pollingInterval);
      } else {
        setError(t('sms.updateNumberError'));
      }
    } catch (err) {
      setError(t('sms.updateNumberError'));
      console.error('Error updating number from BMG:', err);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div>
      {showRegisterForm ? (
        <Register isSmsContext={true} iinAndBin={iin}/>
      ) : (
        <Paper classes={{ root: styles.root }}>
          <div className={styles.tab}>
            <AuthTabs activeTab={activeTab} setActiveTab={setActiveTab} />
          </div>

          {showEmailInput && (
            <div className={styles.emailConfirmationContainer}>
              <h2>{t('emailConfirmation.title')}</h2>
              <p>{t('emailConfirmation.enterEmail')}</p>
              <div className={styles.emailConfirmationContent}>
                <TextField
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  variant="outlined"
                  margin="normal"
                  onKeyDown={handleEmailKeyDown}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleConfirmEmail} 
                >
                  {t('emailConfirmation.sendCode')}
                </Button>
              </div>
            </div>
          )}

          {!isEmailConfirmationVisible && !isPasswordFormVisible && !showLoginForm && !isCodeFormVisible && !showEmailInput && !isVerificationCodeInput ? (
            <div className={styles.smsContainer}>
              <h2 className={styles.enterIin}>{t('sms.enterIin')}</h2>
              <TextField
                label={t('sms.iin')}
                variant="outlined"
                fullWidth
                margin="normal"
                value={iin}
                onChange={handleInputChange}
                error={!!error}
                helperText={error}
                onKeyDown={handleIinKeyDown}
              />
              {isLoading ? (
                <CircularProgress style={{ marginTop: "50px", marginInline: "auto" }} />
              ) : (
                <>
                  {!firstRequestSent && (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleButtonClick}
                      disabled={isButtonDisabled || isCheckingBMG}
                      className={styles.getSmsCode}
                    >
                      {t('sms.getSmsCode')}
                    </Button>
                  )}
                  {firstRequestSent && timer > 0 && (
                    <p>{t('sms.resendCodeIn')} {timer} сек.</p>
                  )}
                  {firstRequestSent && timer === 0 && (
                    <Button
                      variant="outlined"
                      color="secondary"
                      onClick={handleResendClick}
                      style={{ width: "300px", marginTop: "20px" }}
                      disabled={isButtonDisabled}
                    >
                      {t('sms.resendCode')}
                    </Button>
                  )}
                </>
              )}
              {
                /*successMessage && <p style={{ color: 'green' }}>{successMessage}</p>*/
              }
            </div>
          ) : isEmailConfirmationVisible ? (
            <EmailConfirmation 
              email={email}
              onConfirm={handleConfirmEmail}
              onChangeEmail={handleChangeEmail}
            />
          ) : isPasswordFormVisible ? (
            <div className={styles.smsContainer}>
              <div>
                <div>
                  <p className={styles.loginTitle}>{t('sms.iinVerified')}</p>
                  <p className={styles.loginLabel}>{t('sms.enterPassword')}</p>
                </div>
                <PasswordForm
                  onSubmit={handleLoginClick}
                  isLoading={isLoading}
                  errorMessage={error}
                  passwordLabel={t('login.password')}
                  submitButtonText={t('login.loginButton')}
                />
              </div>
            </div>
          ) : isCodeFormVisible ? (
            <div className={styles.smsContainer}>
              <p>{t('sms.enterCodeInstruction')}{lastDigits && ` (${lastDigits})`}</p>
              <Button 
                variant="outlined" 
                color="primary" 
                onClick={handleUpdateNumberFromBMG}
                disabled={isUpdateNumberDisabled || isLoading}
                style={{ marginBottom: '10px' }}
              >
                {t('sms.updateNumberFromBMG')}
              </Button>
              <TextField
                label={t('register.codePlaceholder')}
                variant="outlined"
                fullWidth
                margin="normal"
                value={code}
                onChange={(e) => setCode(sanitizeCode(e.target.value))}
                inputProps={{ maxLength: CODE_LENGTH, inputMode: 'numeric', pattern: '[0-9]*' }}
                error={!!codeError}
                helperText={codeError}
                onKeyDown={handleCodeKeyDown}
              />
              <p>{resendTimer > 0 ? `${t('sms.resendCodeIn')} ${resendTimer} сек.` : ''}</p>
              <div style={{ display: 'flex', justifyContent: 'space-around', marginTop: '10px'}}>
                <Button 
                  variant="outlined" 
                  color="primary" 
                  onClick={handleResendCode}
                  disabled={isResendDisabled}
                >
                  {t('sms.getNewCode')}
                </Button>
                <Button variant="contained" color="primary" onClick={() => handleCodeSubmit(code)}>
                  {t('sms.confirmCode')}
                </Button>
              </div>
              {/* <Button onClick={handleGoHome} style={{ marginTop: '20px', display: 'block', textAlign: 'center', textDecoration: 'underline', color: '#000', marginInline: 'auto' }}>
                {t('common.goHome')}
              </Button> */}
            </div>
          ) : isVerificationCodeInput ? (
            <VerificationCodeInput
              onSubmit={handleCodeSubmit}
              onResend={handleConfirmEmail}
              showResendButton={showResendButton}
              errorMessage={codeError}
            />  
          ) : null}
        </Paper>
      )}
      <p style={{ fontSize: "12px", textAlign: "center" }}>{t('footer.disclaimer1')}</p>
      <p style={{ fontSize: "12px", textAlign: "center" }}>{t('footer.disclaimer2')}</p>
    </div>
  );
};